import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Warehouse } from '@/api/entities';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Edit, Trash2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/components/ui/use-toast";
import WarehouseForm from './WarehouseForm';
import { formatAddress } from '@/utils';

export default function WarehousesView() {
    const { t } = useTranslation();
    const [warehouses, setWarehouses] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [warehouseToDelete, setWarehouseToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const { toast } = useToast();

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await Warehouse.list({ is_active: true });
            setWarehouses(data);
        } catch (error) {
            console.error("Error fetching warehouses:", error);
            toast({
                title: t('errors.error'),
                description: t('inventory.warehouses.error.load_failed'),
                variant: "destructive",
            });
        }
        setIsLoading(false);
    }, [toast]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleEdit = (warehouse) => {
        setSelectedWarehouse(warehouse);
        setIsModalOpen(true);
    };

    const handleCreate = () => {
        setSelectedWarehouse(null);
        setIsModalOpen(true);
    };

    const handleSave = () => {
        fetchData();
        setIsModalOpen(false);
    };

    const handleDeleteClick = (warehouse) => {
        setWarehouseToDelete(warehouse);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!warehouseToDelete) return;

        setIsDeleting(true);
        try {
            console.log('Deleting warehouse:', warehouseToDelete.id);
            await Warehouse.delete(warehouseToDelete.id);
            
            console.log('Warehouse deleted successfully, refreshing list...');
            
            toast({
                title: t('notifications.success.success'),
                description: t('inventory.warehouses.success.deleted', { name: warehouseToDelete.name }),
            });
            
            // Force refresh the list
            setIsLoading(true);
            try {
                const data = await Warehouse.list({ is_active: true });
                console.log('Refreshed warehouse list:', data);
                setWarehouses(data);
            } catch (refreshError) {
                console.error("Error refreshing warehouse list:", refreshError);
                toast({
                    title: t('notifications.warning.warning'),
                    description: t('inventory.warehouses.error.load_failed'),
                    variant: "destructive",
                });
            } finally {
                setIsLoading(false);
            }
            
        } catch (error) {
            console.error("Error deleting warehouse:", error);
            toast({
                title: t('errors.error'),
                description: t('inventory.warehouses.error.delete_failed'),
                variant: "destructive",
            });
        } finally {
            setIsDeleting(false);
            setDeleteDialogOpen(false);
            setWarehouseToDelete(null);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setWarehouseToDelete(null);
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-slate-800">{t('inventory.warehouses.title')}</h2>
                <Button onClick={handleCreate} className="bg-amber-600 hover:bg-amber-700">
                    <Plus className="w-4 h-4 mr-2" />
                    {t('inventory.warehouses.add_warehouse')}
                </Button>
            </div>

            <Card className="elegant-card">
              <CardContent className="p-0">
                {isLoading ? (
                  <div className="p-8 text-center text-slate-500">
                    {t('inventory.warehouses.loading')}
                  </div>
                ) : warehouses.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    {t('inventory.warehouses.not_found')}
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('inventory.warehouses.table.name')}</TableHead>
                        <TableHead>{t('inventory.warehouses.table.address')}</TableHead>
                        <TableHead>{t('inventory.warehouses.table.status')}</TableHead>
                        <TableHead className="text-right">{t('inventory.warehouses.table.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {warehouses.map(w => (
                        <TableRow key={w.id}>
                          <TableCell className="font-medium">{w.name}</TableCell>
                          <TableCell>{formatAddress(w.address)}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${
                              w.is_active 
                                ? 'bg-green-600 text-white border-green-700' 
                                : 'bg-red-600 text-white border-red-700'
                            }`}>
                              {w.is_active ? t('inventory.warehouses.status.active') : t('inventory.warehouses.status.inactive')}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button variant="ghost" size="icon" onClick={() => handleEdit(w)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => handleDeleteClick(w)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            <WarehouseForm
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                warehouse={selectedWarehouse}
                onSave={handleSave}
            />

            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('inventory.warehouses.delete_dialog.title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('inventory.warehouses.delete_dialog.description', { name: warehouseToDelete?.name })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={handleDeleteCancel} disabled={isDeleting}>
                            {t('inventory.warehouses.delete_dialog.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={handleDeleteConfirm} 
                            disabled={isDeleting}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {isDeleting ? t('inventory.warehouses.delete_dialog.deleting') : t('inventory.warehouses.delete_dialog.delete')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}